import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AUSTRALIAN_TIMEZONE } from "./constants/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

// Hardcoded Australian timezone (Australia/Melbourne)
export const userTimezone = AUSTRALIAN_TIMEZONE;

/**
 * Format a date/time string to "DD MMM YYYY, hh:mm A" in Australian timezone (12-hour format).
 * Example: "19 Aug 2026, 05:07 PM"
 *
 * @param {string|Date} value - ISO date string or Date object
 * @param {string} [fallback=""] - Fallback string if value is invalid
 * @returns {string}
 */
export function formatDateTime(value, fallback = "") {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.tz(AUSTRALIAN_TIMEZONE).format("DD MMM YYYY, hh:mm A ([AEST])");
}

/**
 * Format a date string to "DD MMM YYYY" in Australian timezone (date only, no time).
 * Example: "19 Aug 2026"
 *
 * @param {string|Date} value - ISO date string or Date object
 * @param {string} [fallback=""] - Fallback string if value is invalid
 * @returns {string}
 */
export function formatDateOnly(value, fallback = "") {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.tz(AUSTRALIAN_TIMEZONE).format("DD MMM YYYY ([AEST])");
}

/**
 * Format a date string to "hh:mm A (AEST)" in Australian timezone (time only, 12-hour).
 * Example: "05:07 PM (AEST)"
 *
 * @param {string|Date} value - ISO date string or Date object
 * @param {string} [fallback=""] - Fallback string if value is invalid
 * @returns {string}
 */
export function formatTimeOnly(value, fallback = "") {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.tz(AUSTRALIAN_TIMEZONE).format("hh:mm A ([AEST])");
}

/**
 * Format a date as a relative time ago string or full date for older items.
 * Used for notification-style "2m ago", "5h ago", "3d ago" displays.
 * Falls back to "DD MMM YYYY, hh:mm A (AEST)" (12-hour) for items older than 7 days.
 *
 * @param {string|Date} value - ISO date string or Date object
 * @returns {string}
 */
export function formatTimeAgo(value) {
  if (!value) return "";
  const d = dayjs(value);
  if (!d.isValid()) return "";
  const now = dayjs();
  const diffInSeconds = Math.floor(now.diff(d, "second"));

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return d.tz(AUSTRALIAN_TIMEZONE).format("DD MMM YYYY, hh:mm A ([AEST])");
}
